import { LANGUAGE_CATALOG, formatCount, tt } from '@agos/shared'

/**
 * `/settings-ai-translate` — which languages the machine translates into, and two bulk runs.
 *
 * **FB-161 (security).** The page reads `GET /settings/basic` for one reason:
 *
 * ```tsx
 * setHasApiKey(!!b.anthropicApiKey);
 * ```
 *
 * That is FB-132's endpoint — the one that answers a **live Anthropic API key** to any `admin` — pulled into
 * the browser to test whether a key exists. And `GET /ai/translate-status` already answers exactly that
 * question and nothing more:
 *
 * ```json
 * { "ok": true, "hasApiKey": true, "autoTranslateEnabled": false,
 *   "autoTranslateLanguages": ["sk","hu","en","it"], "supportedLanguages": [ …twelve… ] }
 * ```
 *
 * So the page reads the status endpoint, and the credential never enters this screen at all.
 *
 * **FB-162.** `hasApiKey` starts as `true`, so before the first response the page states that a key is
 * configured. An unknown must not render as the reassuring answer.
 *
 * **FB-163.** One bulk run asks and the other does not. `runBulkProducts` opens a `window.confirm`;
 * `runBulkCategories` fires on the click. Both spend Anthropic tokens on every product or category they touch.
 * And `pollJob` is
 *
 * ```tsx
 * while (true) { await sleep(1500); … if (j.status === "done" || j.status === "error") return j; }
 * ```
 *
 * — an unbounded loop with no abort, held by the click handler, so a job that never reports `done` polls every
 * 1,5 seconds for as long as the tab is open.
 */

export interface AiStatus {
  hasApiKey: boolean
  autoTranslateEnabled: boolean
  autoTranslateLanguages: string[]
  supportedLanguages: string[]
}

/** The endpoint that answers whether a key exists without carrying it. */
export function statusOf(payload: unknown): AiStatus | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
  return {
    hasApiKey: row.hasApiKey === true,
    autoTranslateEnabled: row.autoTranslateEnabled === true,
    autoTranslateLanguages: strings(row.autoTranslateLanguages),
    supportedLanguages: strings(row.supportedLanguages),
  }
}

/** The field that must never be read on this page. */
export const CREDENTIAL_FIELD = 'anthropicApiKey'

export type KeyState = 'unknown' | 'present' | 'absent'

/**
 * FB-162: three states, and *unknown* is one of them.
 *
 * Legacy starts at `true`, so the page says a key is configured until told otherwise — the reassuring answer
 * given before anything is known.
 */
export function keyState(status: AiStatus | null): KeyState {
  if (status === null) return 'unknown'
  return status.hasApiKey ? 'present' : 'absent'
}

export function keyMessage(state: KeyState): string {
  if (state === 'unknown') return tt('Či je kľúč k AI nastavený, sa ešte zisťuje.')
  if (state === 'absent') {
    return tt('Kľúč k AI nie je nastavený - nepreloží sa nič, ani automaticky, ani hromadne.')
  }
  return tt('Kľúč k AI je nastavený. Táto obrazovka ho nečíta ani nezobrazuje - spravuje sa v centrále.')
}

// ─── The languages ───────────────────────────────────────────────────────────

export interface LanguageChoice {
  code: string
  label: string
  flag: string
  /** Whether the backend lists it as translatable. */
  supported: boolean
}

/**
 * Every language the catalogue knows, marked with whether the backend supports it.
 *
 * The two lists match today — twelve each — and marking rather than filtering means a language the backend
 * drops shows as unsupported instead of vanishing from a screen that had it selected.
 */
export function languageChoices(status: AiStatus | null): LanguageChoice[] {
  const supported = new Set(status?.supportedLanguages ?? [])
  return LANGUAGE_CATALOG.map((language) => ({
    code: language.code,
    label: language.label,
    flag: language.flag,
    supported: supported.size === 0 || supported.has(language.code),
  }))
}

export interface AiForm {
  autoEnabled: boolean
  languages: string[]
  defaultLanguage: string
}

export function formOf(status: AiStatus | null): AiForm {
  const languages = status?.autoTranslateLanguages ?? []
  return {
    autoEnabled: status?.autoTranslateEnabled === true,
    languages: languages.length > 0 ? [...languages] : ['sk'],
    // The source language is the one the memory translates *from*; it is always in the list.
    defaultLanguage: languages.includes('sk') ? 'sk' : (languages[0] ?? 'sk'),
  }
}

export function toggleLanguage(form: AiForm, code: string): AiForm {
  const has = form.languages.includes(code)
  const languages = has
    ? form.languages.filter((entry) => entry !== code)
    : [...form.languages, code]
  return {
    ...form,
    languages,
    // Removing the default leaves nothing to translate from, so it moves to whatever is left.
    defaultLanguage: languages.includes(form.defaultLanguage)
      ? form.defaultLanguage
      : (languages[0] ?? ''),
  }
}

export type FormProblem = 'no-language' | 'default-not-selected' | null

export function formProblem(form: AiForm): FormProblem {
  if (form.languages.length === 0) return 'no-language'
  if (!form.languages.includes(form.defaultLanguage)) return 'default-not-selected'
  return null
}

export function formProblemMessage(problem: FormProblem): string {
  if (problem === 'no-language') return tt('Vyberte aspoň jeden jazyk.')
  if (problem === 'default-not-selected') {
    return tt('Zdrojový jazyk musí byť medzi vybranými.')
  }
  return ''
}

/**
 * The three keys this page writes into `basic_settings`.
 *
 * The section merges, and the API key is **not** among them — legacy says so in a comment and the port keeps
 * it, with the difference that the key is never read either.
 */
export function payloadOf(form: AiForm): Record<string, unknown> {
  return {
    autoTranslateEnabled: form.autoEnabled,
    autoTranslateLanguages: form.languages,
    defaultLanguage: form.defaultLanguage,
  }
}

/** What switching automatic translation on actually means, in tokens. */
export function autoNote(form: AiForm, state: KeyState): string {
  if (state !== 'present') return keyMessage(state)
  if (!form.autoEnabled) {
    return tt('Nový text sa nepreloží sám - kým to nezapnete, prekladá sa len hromadne alebo ručne.')
  }
  const targets = form.languages.filter((code) => code !== form.defaultLanguage).length
  return `${tt('Každý nový text sa preloží do')} ${formatCount(targets)} ${tt('jazykov cez AI, čo sa účtuje za každý preklad.')}`
}

// ─── FB-163: the two bulk runs ───────────────────────────────────────────────

/**
 * The bulk runs this page can start.
 *
 * **`strings` is §9.305** - the sweep over `translation_memory`, which is the interface itself.
 * Legacy has all three and AGOS had wired the first two; the third's endpoint, job and typed status
 * existed with nothing to start them, so a franchise could translate its menu and not its screens.
 */
export type BulkKind = 'products' | 'categories' | 'strings'

export function bulkLabel(kind: BulkKind): string {
  if (kind === 'products') return tt('Preložiť všetky produkty')
  if (kind === 'categories') return tt('Preložiť všetky kategórie')
  return tt('Doplniť chýbajúce preklady rozhrania')
}

/** Every run costs money per item, so every one is confirmed - legacy asks about one of the three. */
export function bulkConfirmation(kind: BulkKind, form: AiForm): string {
  const targets = form.languages.filter((code) => code !== form.defaultLanguage).length
  const what =
    kind === 'products'
      ? tt('všetky produkty')
      : kind === 'categories'
        ? tt('všetky kategórie')
        : tt('všetky chýbajúce texty rozhrania')
  return `${tt('Preloží')} ${what} ${tt('do')} ${formatCount(targets)} ${tt('jazykov cez AI. Každý preklad sa účtuje a beh sa nedá zastaviť.')}`
}

export interface JobProgress {
  status: string
  done: number
  total: number
  translated: number
  skipped: number
  errors: number
  message: string | null
  /**
   * Strings the sweep has found so far - §9.305, and only the `strings` run reports it.
   *
   * It **grows while the run goes**, because that sweep discovers as it translates rather than
   * counting first. So a progress line that reads `done / total` alone is misleading early on, when
   * `total` is still climbing.
   */
  discovered: number
}

export function jobOf(payload: unknown): JobProgress {
  const row = (payload ?? {}) as Record<string, unknown>
  const number = (value: unknown) => (typeof value === 'number' ? value : 0)
  return {
    status: typeof row.status === 'string' ? row.status : 'running',
    done: number(row.done),
    total: number(row.total),
    translated: number(row.translated),
    skipped: number(row.skipped),
    errors: number(row.errors),
    message: typeof row.message === 'string' && row.message !== '' ? row.message : null,
    discovered: number(row.discovered),
  }
}

export function jobFinished(job: JobProgress): boolean {
  return job.status === 'done' || job.status === 'error'
}

/** 1,5 s, as legacy polls — and bounded, which legacy is not. */
export const JOB_POLL_MS = 1_500

/**
 * How long to keep asking.
 *
 * Legacy's `while (true)` has no cap and no abort, so a job that never reports `done` polls for as long as the
 * tab is open. Ten minutes at 1,5 s is four hundred attempts, which is longer than any real run and short
 * enough to stop.
 */
export const JOB_POLL_LIMIT = 400

export function jobGaveUp(attempts: number): boolean {
  return attempts >= JOB_POLL_LIMIT
}

export function jobSummary(job: JobProgress): string {
  const parts = [
    `${tt('preložených')} ${formatCount(job.translated)}`,
    `${tt('preskočených')} ${formatCount(job.skipped)}`,
  ]
  if (job.errors > 0) parts.push(`${tt('chýb')} ${formatCount(job.errors)}`)
  return `${parts.join(' · ')} ${tt('z')} ${formatCount(job.total)}`
}

export function progressPercent(job: JobProgress): number | null {
  if (job.total <= 0) return null
  return Math.min(100, (job.done / job.total) * 100)
}
